import { useEffect, useRef, useState } from 'react';

import {
    type ClarifyCatalog,
    type ClarifyCatalogAnswer,
    type ProviderGroup,
    parseProviderConfigError,
    useClarifyCatalog,
} from '@/domain/providerConfig';
import { serverErrorView } from '@/routes/shared/ProviderConfig/form/serverErrors';

import type { FormDiff } from '../form/formDiff';

export interface CatalogLoader {
    catalog: ClarifyCatalog | null;
    /**
     * The copy key of what the last listing came to, said next to Load Clarify items:
     * why Clarify gave no items, our own refusal, or that the connection changed after or
     * during a load. Null while there is nothing to say.
     */
    message: string | null;
    load: () => Promise<void>;
    /** Before the form starts over from a save of `saved`: what it stored is what was typed. */
    keepThroughSave: (saved: FormDiff) => void;
}

/**
 * What a catalog was listed with: the connection the form leaves, saved values included so a
 * save that only maps pens keeps the items, and the typed secrets. It holds those as the form
 * does, and is dropped as soon as the form no longer matches it.
 */
function inputsOf(group: ProviderGroup | null, diff: FormDiff): string {
    const saved = Object.fromEntries(
        Object.entries(group?.connection ?? {}).map(([key, state]) => [key, state.value])
    );
    return JSON.stringify({ connection: { ...saved, ...diff.connection }, secrets: diff.secrets });
}

/** What the page says when Clarify gave no items: its reason when it has one, else the status. */
function failureKey(answer: ClarifyCatalogAnswer): string {
    return `providerConfig.pens.catalogFailed.${answer.reason ?? answer.status}`;
}

/** Lists the Clarify items with the connection as the form holds it, saved or not. */
export function useCatalogLoader(
    locationId: number,
    group: ProviderGroup | null,
    diff: FormDiff
): CatalogLoader {
    const { load: loadCatalog, reset } = useClarifyCatalog(locationId);
    const [catalog, setCatalog] = useState<ClarifyCatalog | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const inputs = inputsOf(group, diff);
    const current = useRef(inputs);
    current.current = inputs;
    const loadedWith = useRef<string | null>(null);

    // Items listed with other credentials or another account are not this connection's.
    useEffect(() => {
        if (loadedWith.current === null || loadedWith.current === inputs) return;
        loadedWith.current = null;
        setCatalog(null);
        setMessage('providerConfig.pens.catalogCleared');
    }, [inputs]);

    function keepThroughSave(saved: FormDiff): void {
        if (loadedWith.current !== inputsOf(group, saved)) return;
        // The save stores the typed secrets and connection the items were listed with, so
        // the form left after it, with nothing typed, still matches them.
        loadedWith.current = inputsOf(group, { ...saved, secrets: {} });
    }

    async function load(): Promise<void> {
        setMessage(null);
        const requested = current.current;
        try {
            const loaded = await loadCatalog({
                connection: diff.connection,
                secrets: diff.secrets,
            });
            // The form moved on while the request ran, so the answer is for other inputs.
            if (current.current !== requested) {
                setMessage('providerConfig.pens.catalogRaced');
                return;
            }
            if (loaded.status !== 'ok') {
                setMessage(failureKey(loaded));
                return;
            }
            loadedWith.current = requested;
            setCatalog({ items: loaded.items, truncated: loaded.truncated });
        } catch (failure) {
            const view = serverErrorView(parseProviderConfigError(failure));
            setMessage(view.formMessageKey ?? 'providerConfig.errors.generic');
        } finally {
            // The outcome is copied above; the cached request still holds typed secrets.
            reset();
        }
    }

    return { catalog, message, load, keepThroughSave };
}
